<template>
  <FusionBaseDialog
    :open="open"
    @close="close"
    data-cy="accessAdditionalFeaturesDialog"
    persistentOverlay
    :title="$t('accessAdditionalFeaturesDialog.title')"
    padded
  >
    {{ $t('accessAdditionalFeaturesDialog.message') }}
    <v-checkbox
      color="primary"
      v-model="isAddToBoth"
      @update:model-value="change"
      :label="$t('accessAdditionalFeaturesDialog.addToBoth')"
      class="ml-n2"
      data-cy="addToBothCheckbox"
    ></v-checkbox>
    <div
      v-for="model in subTierSelectByDeviceModel"
      :key="model"
      class="subscription-tier-select-group"
    >
      <v-card-subtitle>
        {{
          $t('accessAdditionalFeaturesDialog.selectTitle', {
            modelName: deviceModelNames[model],
            subscription: $t('activateDevice.subscription')
          })
        }}
      </v-card-subtitle>
      <v-select
        v-model="subTiers[model]"
        :items="subscriptionTierItems"
        item-title="text"
        item-value="value"
        :label="$t('activateDevice.subscriptionTier')"
        :hint="selectHintByModel[model]"
        persistent-hint
        :rules="[rules.required]"
        @update:model-value="subTierChange"
        data-cy="apiSubscriptionTierSelect"
        class="text-field-required mt-1 pt-3"
      ></v-select>
    </div>
    <template v-slot:actions>
      <v-btn
        v-if="xs"
        @click="cancel"
        variant="outlined"
        class="text-black dialog-btn"
        data-cy="accessAdditionalFeaturesCancel"
        >{{ $t('buttons.cancel') }}</v-btn
      >
      <v-spacer />
      <v-btn
        :disabled="!fieldsValid || isClosing"
        :loading="isClosing"
        @click="close"
        class="bg-primary text-white"
        variant="text"
        data-cy="accessAdditionalFeaturesClose"
      >
        {{ $t('buttons.done') }}
      </v-btn>
    </template>
  </FusionBaseDialog>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { useDisplay } from 'vuetify'
import { NAMESPACES } from '@/store/types'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { SUBSCRIPTIONS_GETTERS } from '@/store/subscriptions'
import { deviceModels } from '@/store/subscriptions/utils'

export default defineComponent({
  name: 'AccessAdditionalFeaturesDialog',
  setup() {
    const { xs } = useDisplay()
    return { xs }
  },
  props: {
    open: Boolean,
    addToBoth: { type: Boolean, default: false },
    subscriptionTiers: {
      type: Object as PropType<Record<string, string | null>>,
      default: () => ({})
    },
    bulkDeviceModels: { type: Array as PropType<string[]>, default: (): string[] => [] }
  },
  emits: ['change', 'subTierChange', 'close', 'cancel'],
  data() {
    return {
      isClosing: false,
      isAddToBoth: this.addToBoth,
      subTiers: this.subscriptionTiers as Record<string, string | null>,
      subscriptionTierItems: [
        {
          text: this.$t('activateDevice.subscriptionTierOptions.basic'),
          value: 'basic'
        },
        {
          text: this.$t('activateDevice.subscriptionTierOptions.premium'),
          value: 'premium'
        }
      ],
      rules: {
        required: (value: string | null): boolean | string =>
          !!value || this.$t('validation.required')
      }
    }
  },
  computed: {
    fieldsValid(): boolean {
      let subTierValid = true
      if (this.isAddToBoth && this.subTierSelectByDeviceModel.length > 0) {
        Object.keys(this.subTiers).forEach((key) => {
          if (
            this.subTierSelectByDeviceModel.includes(key) &&
            this.rules.required(this.subTiers[key]) !== true
          ) {
            subTierValid = false
          }
        })
      }
      return subTierValid
    },
    deviceModelNumbers(): string[] {
      return this.$store.getters[`${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODELS}`]
    },
    deviceModelNames(): Record<string, string> {
      return this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_NAMES_MAP}`
      ]
    },
    bulkDeviceModelsSorted(): string[] {
      const orderedByName = Object.values(this.deviceModelNames).sort()
      const sortBy = orderedByName.map((name) => {
        return Object.keys(this.deviceModelNames).find((key) => this.deviceModelNames[key] === name)
      })
      return [...this.bulkDeviceModels].sort(function (a, b) {
        return sortBy.indexOf(a) - sortBy.indexOf(b)
      })
    },
    canAddToBasicSub(): Record<string, boolean> {
      const items: Record<string, boolean> = {}
      this.deviceModelNumbers.forEach((modelNumber) => {
        items[modelNumber] =
          this.$store.getters[
            `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_BASIC_BY_MODEL}`
          ](modelNumber)
      })
      return items
    },
    canAddToPremiumSub(): Record<string, boolean> {
      const items: Record<string, boolean> = {}
      this.deviceModelNumbers.forEach((modelNumber) => {
        items[modelNumber] =
          this.$store.getters[
            `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_PREMIUM_BY_MODEL}`
          ](modelNumber)
      })
      return items
    },
    showSubTierSelect(): Record<string, boolean> {
      const items: Record<string, boolean> = {}
      this.deviceModelNumbers.forEach((modelNumber) => {
        items[modelNumber] =
          this.isAddToBoth &&
          this.canAddToBasicSub[modelNumber] &&
          this.canAddToPremiumSub[modelNumber]
      })
      return items
    },
    selectHintByModel(): Record<string, string> {
      const items: Record<string, string> = {}
      this.deviceModelNumbers.forEach((modelNumber) => {
        if (modelNumber === deviceModels.AIRASSURE) {
          const modelName = this.deviceModelNames[modelNumber]
          const hint = this.$t(`activateDevice.subscriptionTierHint${modelName}`)
          items[modelNumber] = hint
        } else {
          items[modelNumber] = ''
        }
      })
      return items
    },
    subTierSelectByDeviceModel(): string[] {
      return this.bulkDeviceModelsSorted.filter((model) => this.showSubTierSelect[model])
    }
  },
  watch: {
    addToBoth() {
      this.isAddToBoth = this.addToBoth
    },
    subscriptionTiers() {
      this.subTiers = this.subscriptionTiers
    },
    open() {
      this.isClosing = false
    }
  },
  methods: {
    change() {
      this.$emit('change', this.isAddToBoth)
    },
    subTierChange() {
      this.$emit('subTierChange', this.subTiers)
    },
    close() {
      this.isClosing = true
      this.$emit('close')
    },
    cancel() {
      this.$emit('cancel')
    }
  }
})
</script>

<style lang="scss" scoped>
.v-dialog {
  .v-card-title {
    font-weight: 300;
  }
  .v-card-subtitle {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    padding: 0px;
  }
  .v-card-actions {
    margin-top: 24px;
  }
}
.subscription-tier-select-group {
  padding-top: 16px;
  & + & {
    margin-top: 24px;
  }
}
</style>
